import { Head } from "@inertiajs/react";
import { BookOpen, TrendingUp, Medal, Heart } from "lucide-react";
import MilestonesCard from "@/components/schools/MilestonesCard";
import ProgressBar from "@/components/shared/ProgressBar";
import StatTile from "@/components/shared/StatTile";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import ParentLayout from "@/components/parents/ParentLayout";
import ParentActions from "@/components/parents/ParentActions";
import PersonalGoalsCard from "@/components/parents/PersonalGoalsCard";
import ReadingLog from "@/components/parents/ReadingLog";
import SupportersList from "@/components/public/SupportersList";
import SchoolProgress from "@/components/public/SchoolProgress";
import { ordinal } from "@/lib/timeAgo";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, percentOf, pluralize } from "@/lib/format";

/**
 * A parent's page for one child: log reading, share their page, and
 * follow how they, their class and the school are doing.
 */
export default function StudentDetail({
  school_name, siblings, claim_another_path, user_contexts,
  student, school, classroom, campaign, ranking, donations, minute_logs,
  milestones, classroom_milestones = [], student_milestones = [],
}) {
  const room = formatRoom(student.classroom_name);

  return (
    <ParentLayout school_name={school_name} siblings={siblings} claim_another_path={claim_another_path} user_contexts={user_contexts} mobileTitle={student.full_name}>
      <Head title={`${student.full_name} · PagePledge`} />

      <div className="space-y-8">
        <header className="space-y-1">
          <p className="text-sm text-muted-foreground">{campaign.name} · {school.name}</p>
          <h1 className="font-display text-3xl font-semibold text-foreground">{student.full_name}</h1>
          <p className="text-muted-foreground">{room}{student.grade && ` · ${formatGrade(student.grade)}`}</p>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <StatTile size="sm" icon={TrendingUp} label="Raised" value={formatMoney(student.raised_cents)} color="primary" />
          <StatTile size="sm" icon={BookOpen} label="Minutes" value={formatNumber(student.minutes_logged)} color="forest" />
          <StatTile size="sm" icon={Medal} label="Class rank" value={ordinal(ranking.rank)} hint="by minutes" />
        </div>

        <ParentActions student={student} schoolName={school.name} campaign={campaign} />

        {!campaign.finished && <PersonalGoalsCard student={student} />}

        {student_milestones.length > 0 && (
          <MilestonesCard
            milestones={student_milestones}
            manage_path={null}
            title={`Prizes ${student.first_name} can earn`}
            unlockedSummary={({ unlocked, total, entries = 0 }) =>
              `${unlocked} of ${total} earned${entries > 0 ? ` · ${pluralize(entries, "entry", "entries")} in prize draws` : ""}.`
            }
          />
        )}

        <ClassroomProgress classroom={classroom} room={room} />
        {classroom_milestones.length > 0 && (
          <MilestonesCard
            milestones={classroom_milestones}
            manage_path={null}
            title={`${room} rewards`}
            unlockedSummary={({ unlocked, total }) => `${unlocked} of ${total} unlocked — rewards the class earns together.`}
          />
        )}

        <SchoolProgress school={school} campaign={campaign} raisedCents={campaign.raised_cents} minutesLogged={campaign.minutes_logged} />
        {milestones.length > 0 && <MilestonesCard milestones={milestones} manage_path={null} title="School rewards" />}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-primary" /> Donations</CardTitle>
            <CardDescription>
              {donations.length === 0 ? "None yet." : `${pluralize(donations.length, "donation")} · ${formatMoney(student.raised_cents)}`}
            </CardDescription>
          </CardHeader>
          {donations.length > 0 && (
            <CardContent>
              <SupportersList donations={donations} limit={50} />
            </CardContent>
          )}
        </Card>

        <ReadingLog logs={minute_logs} totalMinutes={student.minutes_logged} firstName={student.first_name} />
      </div>
    </ParentLayout>
  );
}

// Only when the teacher has set classroom goals.
function ClassroomProgress({ classroom, room }) {
  if (!classroom?.money_goal_cents && !classroom?.minute_goal) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">How {room} is doing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {classroom.money_goal_cents && (
          <ProgressBar
            label="Raised"
            detail={`${formatMoney(classroom.raised_cents)} / ${formatMoney(classroom.money_goal_cents)}`}
            pct={percentOf(classroom.raised_cents, classroom.money_goal_cents)}
          />
        )}
        {classroom.minute_goal && (
          <ProgressBar
            label="Minutes read"
            detail={`${formatNumber(classroom.minutes_logged)} / ${formatNumber(classroom.minute_goal)}`}
            pct={percentOf(classroom.minutes_logged, classroom.minute_goal)}
            barColor="bg-accent"
          />
        )}
      </CardContent>
    </Card>
  );
}
